import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@hweeks/kei-costs-ui-testids';
import { useEffect, useState } from 'react';
import { Button, ButtonLink, Card, Field, Input, PageTitle, Seal, SectionLabel, Select, ToggleGroup } from '../../design';
import { pagePricing, priced, pricingText } from '../../lib/pricing';
import { AnalyticsEvent, track } from '../../utils/analytics';
import { audienceFrom, CONTACT_EMAIL, MKT } from './links';
import { MarketingLayout } from './MarketingLayout';
import './request-access.css';
const SELLS = [
    'From our own inventory',
    'Custom orders for customers',
    'Both'
];
const VOLUMES = [
    '1–10',
    '11–25',
    '26–50',
    '50+'
];
const WHO = [
    {
        value: 'dealer',
        title: 'I run a dealership',
        line: 'Buying to sell, for stock or customers'
    },
    {
        value: 'individual',
        title: 'I’m importing my own',
        line: 'One JDM car or truck, for myself'
    }
];
export default function RequestAccessPage() {
    // Who's asking: ?for=dealer|individual from the page that sent them here. The prerendered
    // page is a dealership's; the link's choice applies once the page is running.
    const [audience, setAudience] = useState('dealer');
    useEffect(()=>{
        const a = audienceFrom(window.location.search);
        if (a) setAudience(a);
    }, []);
    // Once sign-ups are open (Platform → Billing setup), this form is "talk to us first" and the
    // page offers signing up straight away too.
    const [signupsOpen, setSignupsOpen] = useState(false);
    useEffect(()=>{
        let live = true;
        fetch('/api/auth/signup-status').then((r)=>r.ok ? r.json() : null).then((d)=>live && setSignupsOpen(d?.open === true)).catch(()=>undefined);
        return ()=>{
            live = false;
        };
    }, []);
    const [sells, setSells] = useState('Both');
    const [status, setStatus] = useState({
        kind: 'idle'
    });
    const individual = audience === 'individual';
    const t = pricingText(pagePricing());
    const submit = async (e)=>{
        e.preventDefault();
        const f = new FormData(e.currentTarget);
        setStatus({
            kind: 'sending'
        });
        try {
            const res = await fetch('/api/access-requests', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    audience,
                    name: f.get('name'),
                    email: f.get('email'),
                    business: f.get('business') ?? '',
                    website_or_license: individual ? '' : f.get('website'),
                    sells: individual ? '' : sells,
                    vehicles_per_year: individual ? '' : f.get('volume'),
                    agents: f.get('agents'),
                    wants_call: f.get('call') === 'on',
                    company: f.get('company')
                })
            });
            if (res.ok) {
                track(AnalyticsEvent.requestAccessSubmitted, {
                    audience,
                    sells: individual ? '' : sells,
                    volume: individual ? '' : String(f.get('volume') ?? ''),
                    wants_call: f.get('call') === 'on'
                });
                setStatus({
                    kind: 'done'
                });
                return;
            }
            const body = await res.json().catch(()=>({}));
            track(AnalyticsEvent.requestAccessFailed);
            setStatus({
                kind: 'error',
                message: body.error ?? 'Something went wrong.'
            });
        } catch  {
            setStatus({
                kind: 'error',
                message: 'Couldn’t reach us.'
            });
        }
    };
    return /*#__PURE__*/ _jsx(MarketingLayout, {
        testid: Testids.app.marketing.requestAccessRoot,
        children: /*#__PURE__*/ _jsxs("main", {
            className: "rhd-container mk-ra",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "mk-ra__intro",
                    children: [
                        /*#__PURE__*/ _jsx(SectionLabel, {
                            k: "申込",
                            children: "Request access"
                        }),
                        /*#__PURE__*/ _jsx(PageTitle, {
                            children: individual ? 'Let’s get your car home.' : 'Let’s talk about how you buy.'
                        }),
                        individual ? /*#__PURE__*/ _jsxs("p", {
                            className: "mk-ra__lede",
                            children: [
                                "Import my own is for bringing in one JDM car or truck for yourself, in your own name or your LLC’s. Tell us who you are and we’ll set up your account. Signing up is",
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    ...priced('pass.price'),
                                    children: t['pass.price']
                                }),
                                " and covers your first car."
                            ]
                        }) : /*#__PURE__*/ _jsx("p", {
                            className: "mk-ra__lede",
                            children: "Want to talk before you sign up? Tell us a bit about your shop and we’ll set up a short call to go through it with you."
                        }),
                        signupsOpen && /*#__PURE__*/ _jsxs("p", {
                            className: "mk-ra__lede",
                            children: [
                                "Want to start now?",
                                ' ',
                                /*#__PURE__*/ _jsx("a", {
                                    href: MKT.signupFor(audience),
                                    children: individual ? 'Sign up and import my own' : 'Sign up your dealership'
                                }),
                                ' ',
                                "takes a couple of minutes. Or use the form below to talk to us first."
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mk-ra__steps",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-ra__step",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "mk-ra__n",
                                            children: "01"
                                        }),
                                        "We read every request and reply by email"
                                    ]
                                }),
                                individual ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mk-ra__step",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-ra__n",
                                                    children: "02"
                                                }),
                                                "We set up your Import my own account and email you a sign-in link"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mk-ra__step",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-ra__n",
                                                    children: "03"
                                                }),
                                                "The setup guide walks you through your exporter account, the car you want, customs and shipping"
                                            ]
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mk-ra__step",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-ra__n",
                                                    children: "02"
                                                }),
                                                "A 20-minute call about your exporters, volume and paperwork"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mk-ra__step",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-ra__n",
                                                    children: "03"
                                                }),
                                                "We set up your shop with you"
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("p", {
                            className: "mk-ra__more",
                            children: [
                                "Not sure which fits? ",
                                /*#__PURE__*/ _jsx("a", {
                                    href: MKT.forDealers,
                                    children: "For dealers"
                                }),
                                " ·",
                                ' ',
                                /*#__PURE__*/ _jsx("a", {
                                    href: MKT.importMyOwn,
                                    children: "Import my own"
                                }),
                                " · ",
                                /*#__PURE__*/ _jsx("a", {
                                    href: MKT.pricing,
                                    children: "Pricing"
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Card, {
                    shadow: "lg",
                    id: "call",
                    className: "mk-ra__panel",
                    children: status.kind !== 'done' ? /*#__PURE__*/ _jsxs("form", {
                        onSubmit: submit,
                        className: "mk-ra__form",
                        children: [
                            /*#__PURE__*/ _jsxs("fieldset", {
                                className: "mk-ra__fieldset",
                                children: [
                                    /*#__PURE__*/ _jsx("legend", {
                                        className: "mk-ra__legend",
                                        children: "Who’s asking?"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mk-ra__who",
                                        children: WHO.map((w)=>/*#__PURE__*/ _jsxs("button", {
                                                type: "button",
                                                className: "mk-ra__who-opt",
                                                "aria-pressed": audience === w.value,
                                                "data-testid": Testids.app.marketing.requestAccessAudience(w.value),
                                                onClick: ()=>setAudience(w.value),
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "mk-ra__who-title",
                                                        children: w.title
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "mk-ra__who-line",
                                                        children: w.line
                                                    })
                                                ]
                                            }, w.value))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-ra__pair",
                                children: [
                                    /*#__PURE__*/ _jsx(Field, {
                                        label: "Your name ",
                                        hint: "Required",
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            "data-testid": Testids.app.marketing.requestAccessName,
                                            name: "name",
                                            required: true,
                                            autoComplete: "name"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Field, {
                                        label: individual ? 'Email ' : 'Work email ',
                                        hint: "Required",
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            "data-testid": Testids.app.marketing.requestAccessEmail,
                                            name: "email",
                                            type: "email",
                                            required: true,
                                            autoComplete: "email"
                                        })
                                    })
                                ]
                            }),
                            individual ? /*#__PURE__*/ _jsx(Field, {
                                label: "Importing through an LLC? Its name ",
                                hint: "Optional",
                                children: /*#__PURE__*/ _jsx(Input, {
                                    "data-testid": Testids.app.marketing.requestAccessBusiness,
                                    name: "business",
                                    quiet: true,
                                    autoComplete: "organization",
                                    placeholder: "Leave blank to import in your own name"
                                })
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Field, {
                                        label: "Business name ",
                                        hint: "Required",
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            "data-testid": Testids.app.marketing.requestAccessBusiness,
                                            name: "business",
                                            required: true,
                                            autoComplete: "organization"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Field, {
                                        label: "Website or dealer license number ",
                                        hint: "Optional",
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            name: "website",
                                            quiet: true,
                                            mono: true
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("fieldset", {
                                        className: "mk-ra__fieldset",
                                        children: [
                                            /*#__PURE__*/ _jsx("legend", {
                                                className: "mk-ra__legend",
                                                children: "We sell"
                                            }),
                                            /*#__PURE__*/ _jsx(ToggleGroup, {
                                                options: SELLS,
                                                value: sells,
                                                onChange: setSells,
                                                label: "We sell"
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-ra__pair",
                                children: [
                                    !individual && /*#__PURE__*/ _jsx(Field, {
                                        label: "Vehicles a year, roughly",
                                        children: /*#__PURE__*/ _jsx(Select, {
                                            name: "volume",
                                            defaultValue: VOLUMES[0],
                                            children: VOLUMES.map((v)=>/*#__PURE__*/ _jsx("option", {
                                                    children: v
                                                }, v))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Field, {
                                        label: individual ? 'Exporter account, if you have one' : 'Exporters you use',
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            name: "agents",
                                            quiet: true,
                                            placeholder: individual ? 'JCD, Pacific Coast, or “not yet”' : 'Names, or “not yet”'
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("label", {
                                className: "mk-ra__call",
                                children: [
                                    /*#__PURE__*/ _jsx("input", {
                                        type: "checkbox",
                                        name: "call",
                                        defaultChecked: !individual
                                    }, audience),
                                    /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            /*#__PURE__*/ _jsx("b", {
                                                style: {
                                                    fontWeight: 600
                                                },
                                                children: individual ? 'I’d like a short call too' : 'Set up a 20-minute call'
                                            }),
                                            /*#__PURE__*/ _jsx("br", {}),
                                            /*#__PURE__*/ _jsx("span", {
                                                style: {
                                                    color: 'var(--ink-2)'
                                                },
                                                children: "We’ll email a few times that work."
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mk-ra__hp",
                                "aria-hidden": "true",
                                children: /*#__PURE__*/ _jsxs("label", {
                                    children: [
                                        "Company ",
                                        /*#__PURE__*/ _jsx("input", {
                                            name: "company",
                                            tabIndex: -1,
                                            autoComplete: "off"
                                        })
                                    ]
                                })
                            }),
                            status.kind === 'error' && /*#__PURE__*/ _jsxs("p", {
                                className: "mk-ra__error",
                                role: "alert",
                                "data-testid": Testids.app.marketing.requestAccessError,
                                children: [
                                    status.message,
                                    " You can also email",
                                    ' ',
                                    /*#__PURE__*/ _jsx("a", {
                                        href: `mailto:${CONTACT_EMAIL}`,
                                        children: CONTACT_EMAIL
                                    }),
                                    "."
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                type: "submit",
                                "data-testid": Testids.app.marketing.requestAccessSubmit,
                                block: true,
                                className: "mk-ra__submit",
                                disabled: status.kind === 'sending',
                                children: status.kind === 'sending' ? 'Sending…' : 'Request access'
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                className: "mk-ra__fine",
                                children: [
                                    "We’ll only use this to contact you about Right Hand DMS. Using it is under our",
                                    ' ',
                                    /*#__PURE__*/ _jsx("a", {
                                        href: MKT.terms,
                                        children: "Terms of Service"
                                    }),
                                    "."
                                ]
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs("section", {
                        className: "mk-ra__done",
                        "aria-live": "polite",
                        "data-testid": Testids.app.marketing.requestAccessDone,
                        children: [
                            /*#__PURE__*/ _jsx(Seal, {
                                size: 96,
                                fontSize: 30,
                                border: 3,
                                children: "受付"
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "mk-ra__thanks",
                                children: "Got it, thanks."
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mk-ra__done-text",
                                children: individual ? 'We’ll email you about setting up your account. Meanwhile, the importing guide covers the whole trip, from the auction to the title.' : 'We’ll email you a few times that work for a short call. Meanwhile, have a look at the importing guide.'
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-ra__done-actions",
                                children: [
                                    /*#__PURE__*/ _jsx(ButtonLink, {
                                        href: MKT.guide,
                                        variant: "secondary",
                                        children: "Importing guide"
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        variant: "secondary",
                                        onClick: ()=>setStatus({
                                                kind: 'idle'
                                            }),
                                        children: "← Back to form"
                                    })
                                ]
                            })
                        ]
                    })
                })
            ]
        })
    });
}
